import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeftRight, Minus, Plus, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, Empty, HsId, PageHeader, Row, Section, SidesBar, Spinner, StatusBadge } from "@/components/kit";
import ActivityList from "@/components/ActivityList";
import Documents, { DocList } from "@/components/Documents";
import { TxnRow, SettlementRow } from "@/components/Rows";
import { fdate, firstName, inr, num, pct } from "@/lib/format";

const TABS = ["Overview", "Lots", "History", "Money", "Docs", "Activity"];
const BIZ_ACTIONS = [
  ["capital_contribution", "Capital contribution"], ["additional_investment", "Additional investment"],
  ["ownership_change", "Ownership change"], ["partial_exit", "Partial exit"], ["complete_exit", "Complete exit"],
];

function Ownership({ o }) {
  if (!o.persons.length) return <Empty text="Ownership appears once a transaction is confirmed" testid="ownership-empty" />;
  return (
    <div className="hd-card" data-testid="holding-ownership">
      <SidesBar sides={o.sides} />
      <div className="mt-3">
        {o.persons.map((p) => (
          <Link to={`/person/${p.person_id}`} key={p.person_id} className="flex items-center gap-3 py-2.5 border-b border-[#E2D9CF]/50 last:border-0">
            <Avatar name={p.full_name} id={p.person_id} size={36} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{p.full_name}</div>
              <div className="flex items-center gap-1.5 mt-0.5"><HsId value={p.hs_id} /><span className="text-[11px] text-[#718096]">{p.role === "karta" ? "Karta" : "Coparcener"} · {firstName(p.side_name)} Side</span></div>
            </div>
            <div className="font-bold" data-testid={`owner-pct-${p.hs_id}`}>{pct(p.percentage)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Lots({ lots, unit, hid, category }) {
  if (!lots?.length) return <Empty text="No lots yet" testid="lots-empty" />;
  return (
    <div className="space-y-2.5">
      {lots.map((l) => (
        <div key={l.id} className="hd-card" data-testid={`lot-card-${l.lot_number}`}>
          <div className="flex items-center justify-between">
            <div className="font-bold">Lot #{l.lot_number}</div>
            {l.status !== "confirmed" ? <StatusBadge status={l.status} /> : <span className={`hd-chip ${l.is_open ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{l.is_open ? "Open" : "Fully sold"}</span>}
          </div>
          <div className="text-sm text-[#4A5568] mt-1">{num(l.quantity)} {unit} @ {inr(l.price)} · {fdate(l.date)}</div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <div className="rounded-xl bg-[#F5F0E9] py-2"><div className="text-[11px] text-[#718096]">Remaining</div><div className="font-bold" data-testid={`lot-remaining-${l.lot_number}`}>{num(l.remaining)}</div></div>
            <div className="rounded-xl bg-[#F5F0E9] py-2"><div className="text-[11px] text-[#718096]">Sold</div><div className="font-bold">{num(l.sold_confirmed)}</div></div>
            <div className="rounded-xl bg-[#F5F0E9] py-2"><div className="text-[11px] text-[#718096]">Sale pending</div><div className="font-bold">{num(l.sold_pending)}</div></div>
          </div>
          <div className="text-xs text-[#718096] mt-2">{l.ownership.sides.map((s) => `${firstName(s.name)} ${pct(s.percentage)}`).join(" · ")}</div>
          <div className="flex gap-2 mt-3">
            <Link to={`/txn/${l.transaction_id}`} className="hd-btn-outline h-9 text-xs flex-1" data-testid={`lot-view-${l.lot_number}`}>View purchase</Link>
            {l.is_open && Number(l.available) > 0 && (
              <Link to={`/add/${category}?holding=${hid}&type=sell&lot=${l.id}`} className="hd-btn-ghost h-9 text-xs flex-1" data-testid={`lot-sell-${l.lot_number}`}>Sell from this lot</Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function Money({ d }) {
  const p = d.position;
  return (
    <div className="space-y-4">
      <div className="hd-card">
        <div className="hd-label mb-1">Position on this holding</div>
        <div className="text-lg font-bold" data-testid="holding-position">{p.summary.direction === "settled" ? "All settled" : `${p.summary.text} ${inr(p.summary.amount)}`}</div>
        <div className="text-xs text-[#718096] mt-1">Includes settlements linked to this holding</div>
      </div>
      <Section title="Contributions & receipts" className="mt-0">
        <div className="hd-card py-1">
          {d.contributions.length === 0 ? <div className="text-sm text-[#718096] py-3">None</div> : d.contributions.map((c, i) => (
            <Link to={`/txn/${c.transaction_id}`} key={i} className="flex justify-between py-2.5 border-b border-[#E2D9CF]/50 last:border-0 text-sm">
              <div><div className="font-semibold">{c.full_name} <span className="hs-id text-[11px] text-[#718096]">{c.hs_id}</span></div>
                <div className="text-xs text-[#718096]">{c.direction === "payment" ? "Paid" : "Received"} · {c.type_label} · {fdate(c.date)} · {c.status}</div></div>
              <div className={`font-bold tabular-nums ${c.direction === "payment" ? "" : "text-emerald-700"}`}>{inr(c.amount)}</div>
            </Link>
          ))}
        </div>
      </Section>
      <Section title="Linked settlements" className="mt-0">
        <div className="hd-card py-1">{d.settlements.length === 0 ? <div className="text-sm text-[#718096] py-3">None</div> : d.settlements.map((s) => <SettlementRow key={s.id} s={s} />)}</div>
      </Section>
    </div>
  );
}

export default function HoldingDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("Overview");
  const load = () => api.get(`/holdings/${id}`).then((r) => setD(r.data));
  useEffect(() => {
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <Spinner />;
  const s = d.summary;
  const a = s.asset;
  const isBiz = s.category === "business";
  const tabs = isBiz ? TABS.filter((t) => t !== "Lots") : TABS;
  return (
    <div>
      <PageHeader title={a.name} subtitle={s.category === "stock" ? `${a.symbol} · ${a.exchange}` : isBiz ? `${a.legal_entity_name} · ${a.entity_type}` : `Commodity · ${a.unit}`}
        right={<StatusBadge status={s.status} testid="holding-status" />} />
      <div className="hd-card">
        {!isBiz ? (
          <div className="grid grid-cols-3 gap-3">
            <div><div className="text-xs text-[#718096]">Quantity</div><div className="font-bold text-lg" data-testid="holding-detail-qty">{num(s.quantity)}</div><div className="text-[11px] text-[#718096]">{s.category === "stock" ? "shares" : s.unit}</div></div>
            <div><div className="text-xs text-[#718096]">Avg price</div><div className="font-bold text-lg" data-testid="holding-detail-avg">{inr(s.avg_price)}</div></div>
            <div><div className="text-xs text-[#718096]">Cost (incl. charges)</div><div className="font-bold text-lg">{inr(s.invested)}</div></div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <div><div className="text-xs text-[#718096]">Invested</div><div className="font-bold text-lg">{inr(s.invested)}</div></div>
            <div><div className="text-xs text-[#718096]">Shareholding</div><div className="font-bold text-lg">{s.stake_pct != null ? pct(s.stake_pct) : "—"}</div></div>
            <div><div className="text-xs text-[#718096]">Exit proceeds</div><div className="font-bold text-lg">{inr(s.exit_proceeds)}</div></div>
          </div>
        )}
        {s.status !== "closed" && (
          <div className="flex gap-2 mt-4">
            {!isBiz ? (
              <>
                <Link to={`/add/${s.category}?holding=${s.id}&type=buy`} className="hd-btn-primary h-11 flex-1 text-sm" data-testid="holding-buy-more"><Plus size={16} /> Buy more</Link>
                {s.open_lots > 0 && <Link to={`/add/${s.category}?holding=${s.id}&type=sell`} className="hd-btn-ghost h-11 flex-1 text-sm" data-testid="holding-sell"><Minus size={16} /> Sell</Link>}
              </>
            ) : s.status === "active" ? (
              <select className="hd-input h-11 text-sm font-semibold" data-testid="holding-business-action" value="" onChange={(e) => e.target.value && (window.location.href = `/add/business?holding=${s.id}&type=${e.target.value}`)}>
                <option value="">+ Record a transaction…</option>
                {BIZ_ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : <div className="text-sm text-[#718096]">Waiting for the initial investment to be confirmed.</div>}
            <Link to={`/add/settlement?holding=${s.id}`} className="hd-btn-outline h-11 px-3" data-testid="holding-settle" aria-label="Record settlement"><ArrowLeftRight size={16} /></Link>
          </div>
        )}
      </div>

      <div className="flex gap-1.5 overflow-x-auto mt-4 -mx-4 px-4 pb-1" data-testid="holding-tabs">
        {tabs.map((t) => (
          <button key={t} data-testid={`holding-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold transition-colors ${tab === t ? "bg-[#1A202C] text-white" : "bg-white text-[#4A5568] border border-[#E2D9CF]"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-4">
        {tab === "Overview" && (
          <div className="space-y-4">
            <Ownership o={s.ownership} />
            {!isBiz && s.category === "stock" && (
              <div>
                <div className="hd-label mb-2">Custody</div>
                {d.custody.length === 0 ? <Empty text="No custody recorded" /> : d.custody.map((c) => (
                  <div key={c.id} className="hd-card mb-2 flex items-center gap-3" data-testid={`custody-${c.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#F5F0E9] flex items-center justify-center"><Landmark size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{c.account_name} · {c.broker}</div>
                      <div className="text-xs text-[#718096]">Holder: {c.holder_name} <span className="hs-id">{c.holder_hs_id}</span> · Lots {c.lots.map((n) => `#${n}`).join(", ")}</div>
                    </div>
                  </div>
                ))}
                <div className="text-xs text-[#718096]">Custody shows whose Demat holds the shares — it does not determine ownership.</div>
              </div>
            )}
            <div className="hd-card"><Row label="Added">{fdate(s.created_at.slice(0, 10))}</Row><Row label="Category">{s.category}</Row>{s.pending_count > 0 && <Row label="Pending">{s.pending_count} transaction(s)</Row>}</div>
          </div>
        )}
        {tab === "Lots" && <Lots lots={s.lots} unit={s.category === "stock" ? "shares" : s.unit} hid={s.id} category={s.category} />}
        {tab === "History" && <div className="hd-card py-1" data-testid="holding-history">{d.transactions.length ? d.transactions.map((t) => <TxnRow key={t.id} t={t} />) : <div className="py-3 text-sm text-[#718096]">No transactions</div>}</div>}
        {tab === "Money" && <Money d={d} />}
        {tab === "Docs" && (
          <div className="space-y-4">
            <Documents entityType="holding" entityId={s.id} initial={d.documents.filter((x) => x.entity_type === "holding")} onUploaded={load} />
            <div className="hd-card"><div className="hd-label mb-1">All supporting documents</div><DocList docs={d.documents} /></div>
          </div>
        )}
        {tab === "Activity" && <ActivityList items={d.activity} testid="holding-activity" />}
      </div>
    </div>
  );
}
